import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from './service';
import {AuthContextType, UserInfo, UserRole} from "../../../model";

const AuthContext = createContext<AuthContextType> ({
  isAuthenticated: false,
  userInfo: null,
  login: async () => false,
  logout: () => {},
  hasAccess: () => false,
  hasEditPermission: () => false,
  hasPrintPermission: () => false
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 當用戶重新載入頁面時，先將身份驗證狀態設為在本地存儲中的狀態
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(authService.isAuthenticated());
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  // 從 localStorage 加載用戶信息
  useEffect(() => {
    if (isAuthenticated) {
      const storedUserInfo = localStorage.getItem('user_info');
      if (storedUserInfo) {
        try {
          setUserInfo(JSON.parse(storedUserInfo));
        } catch (error) {
          console.error('Failed to parse user info:', error);
        }
      }
    } else {
      setUserInfo(null);
    }
  }, [isAuthenticated]);

  // 監控身份驗證狀態，啟動或停止活動追蹤
  useEffect(() => {
    // 如果已經登入，啟動活動追蹤
    if (isAuthenticated) {
      authService.startActivityTracking();
    } else {
      authService.stopActivityTracking();
    }
    
    // 當組件卸載時停止追蹤
    return () => {
      authService.stopActivityTracking();
    };
  }, [isAuthenticated]);  // 當身份驗證狀態改變時重新評估

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await authService.login({ username, password });
      const success = !!response?.token;
      
      if (success && response) {
        setIsAuthenticated(true);
        // 存儲用戶信息
        const userInfo: UserInfo = {
          role: parseInt(response.userRole) as UserRole || 1,
        };
        localStorage.setItem('user_info', JSON.stringify(userInfo));
        setUserInfo(userInfo);
      }
      
      return success;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    // 先停止活動追蹤，再調用 authService.logout()
    authService.stopActivityTracking();
    authService.logout();
    localStorage.removeItem('user_info');
    setUserInfo(null);
    setIsAuthenticated(false);
  };

  // 檢查用戶是否有訪問權限
  const hasAccess = (requiredRoles: UserRole[]): boolean => {
    if (!isAuthenticated || !userInfo) return false;
    return requiredRoles.includes(userInfo.role);
  };

  const hasEditPermission = (requiredRoles: UserRole[]): boolean => {
    if(!isAuthenticated || !userInfo) return false;
    return requiredRoles.includes(userInfo.role);
  }

  const hasPrintPermission = (requiredRoles: UserRole[]): boolean => {
    if(!isAuthenticated || !userInfo) return false;
    return requiredRoles.includes(userInfo.role);
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, userInfo, login, logout, hasAccess, hasEditPermission, hasPrintPermission }}>
      {children}
    </AuthContext.Provider>
  );
};
